import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Bell, Lightbulb, Plus, Trash2 } from "lucide-react";
import {
  TablePagePagination,
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  formatOptionalNumber,
  getAlertValueToneClass,
  type AlertRule,
  type CampaignSummary,
} from "./metaTableConfig";
import { formatPaidMediaRoas, paidMediaFixedDigits } from "../../utils/paidMediaNumberFormat";

type TriggeredAlert = {
  id: string;
  ruleId: AlertRule["id"];
  metric: AlertRule["metric"];
  campaignName: string;
  value: number;
};

interface MetaAlertsPanelProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  alerts: AlertRule[];
  setAlerts: Dispatch<SetStateAction<AlertRule[]>>;
  alertRuleLabel: (_id: AlertRule["id"]) => string;
  triggeredAlerts: TriggeredAlert[];
  paginatedTriggeredAlerts: TriggeredAlert[];
  currentTriggeredAlertsPage: number;
  setCurrentTriggeredAlertsPage: Dispatch<SetStateAction<number>>;
  triggeredAlertsTotalPages: number;
  budgetRecommendations: { increase: CampaignSummary[]; reduce: CampaignSummary[] };
  formatMoneyShort: (_value: number) => string;
}

const MetaAlertsPanel: FC<MetaAlertsPanelProps> = ({
  metaSectionHelp,
  alerts,
  setAlerts,
  alertRuleLabel,
  triggeredAlerts,
  paginatedTriggeredAlerts,
  currentTriggeredAlertsPage,
  setCurrentTriggeredAlertsPage,
  triggeredAlertsTotalPages,
  budgetRecommendations,
  formatMoneyShort,
}) => {
  const { t } = useTranslation();

  return (
              <div className="paid-media-alerts">
                <div className="paid-media-alerts__toolbar">
                  <button type="button" className="paid-media-primary-button">
                    <Plus size={16} />
                    {t("metaAdsReport.alerts.newAlert")}
                  </button>
                </div>

                <div className="paid-media-tab-grid paid-media-tab-grid--two">
                  <article className="paid-media-panel">
                    <div className="paid-media-panel__header">
                      <div className="paid-media-panel__heading">
                        <h3 className="paid-media-panel__title-with-icon">
                          <Bell size={16} />
                          {t("metaAdsReport.alerts.rulesTitle")}
                        </h3>
                        <DefinitionTooltip
                          label={t("metaAdsReport.alerts.rulesTitle")}
                          info={metaSectionHelp("alertRules")}
                          formulaTitle={t("webAnalytics.kpi.formula")}
                        />
                      </div>
                    </div>
                    <div className="paid-media-alert-rules">
                      {alerts.map(rule => (
                        <div key={rule.id} className="paid-media-alert-rule">
                          <div className="paid-media-alert-rule__left">
                            <button
                              type="button"
                              className={
                                rule.enabled
                                  ? "paid-media-switch paid-media-switch--on"
                                  : "paid-media-switch"
                              }
                              onClick={() =>
                                setAlerts(current =>
                                  current.map(item =>
                                    item.id === rule.id
                                      ? { ...item, enabled: !item.enabled }
                                      : item
                                  )
                                )
                              }
                              aria-label={t("metaAdsReport.alerts.toggleRule", {
                                name: alertRuleLabel(rule.id),
                              })}
                            >
                              <span />
                            </button>
                            <div>
                              <strong>{alertRuleLabel(rule.id)}</strong>
                              <span>
                                {rule.metric.toUpperCase()} {rule.operator} {rule.threshold}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="paid-media-icon-button"
                            onClick={() =>
                              setAlerts(current =>
                                current.filter(item => item.id !== rule.id)
                              )
                            }
                            aria-label={t("metaAdsReport.alerts.deleteRule", {
                              name: alertRuleLabel(rule.id),
                            })}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </article>

                  <article className="paid-media-panel">
                    <div className="paid-media-panel__header">
                      <div className="paid-media-panel__heading">
                        <h3 className="paid-media-panel__title-with-icon">
                          <AlertTriangle size={16} />
                          {t("metaAdsReport.alerts.triggeredTitle", {
                            count: triggeredAlerts.length,
                          })}
                        </h3>
                        <DefinitionTooltip
                          label={t("metaAdsReport.alerts.triggeredTooltipLabel")}
                          info={metaSectionHelp("triggered")}
                          formulaTitle={t("webAnalytics.kpi.formula")}
                        />
                      </div>
                    </div>
                    <div className="paid-media-triggered-list">
                      {triggeredAlerts.length === 0 ? (
                        <div className="paid-media-triggered-list__empty">
                          {t("metaAdsReport.alerts.noAlerts")}
                        </div>
                      ) : (
                        paginatedTriggeredAlerts.map(item => (
                          <div key={item.id} className="paid-media-triggered-item">
                            <strong>{alertRuleLabel(item.ruleId)}</strong>
                            <span>{item.campaignName}</span>
                            <small className={getAlertValueToneClass(item.metric, item.value)}>
                              {item.metric === "roas"
                                ? t("metaAdsReport.alerts.triggeredRoas", {
                                    value: paidMediaFixedDigits(item.value, 2),
                                  })
                                : item.metric === "cpa"
                                  ? t("metaAdsReport.alerts.triggeredCpa", {
                                      value: formatMoneyShort(item.value),
                                    })
                                  : t("metaAdsReport.alerts.triggeredFrequency", {
                                      value: paidMediaFixedDigits(item.value, 2),
                                    })}
                            </small>
                          </div>
                        ))
                      )}
                    </div>
                    {triggeredAlerts.length > 0 ? (
                      <TablePagePagination
                        currentPage={currentTriggeredAlertsPage}
                        totalPages={triggeredAlertsTotalPages}
                        onPrevious={() =>
                          setCurrentTriggeredAlertsPage(page => Math.max(1, page - 1))
                        }
                        onNext={() =>
                          setCurrentTriggeredAlertsPage(page =>
                            Math.min(triggeredAlertsTotalPages, page + 1)
                          )
                        }
                      />
                    ) : null}
                  </article>
                </div>

                <article className="paid-media-panel">
                  <div className="paid-media-panel__header">
                    <div className="paid-media-panel__heading">
                      <h3 className="paid-media-panel__title-with-icon">
                        <Lightbulb size={16} />
                        {t("metaAdsReport.alerts.budgetRecommendations")}
                      </h3>
                      <DefinitionTooltip
                        label={t("metaAdsReport.alerts.budgetRecommendations")}
                        info={metaSectionHelp("budgetRecommendations")}
                        formulaTitle={t("webAnalytics.kpi.formula")}
                      />
                    </div>
                  </div>
                  <div className="paid-media-recommendations">
                    <div>
                      <div className="paid-media-recommendations__label paid-media-recommendations__label--positive">
                        {t("metaAdsReport.alerts.increaseBudget")}
                      </div>
                      <div className="paid-media-recommendations__stack">
                        {budgetRecommendations.increase.map(item => (
                          <div
                            key={item.id}
                            className="paid-media-recommendation-card paid-media-recommendation-card--positive"
                          >
                            <strong>{item.campaignName}</strong>
                            <span>
                              {t("metaAdsReport.alerts.recRoasCpa", {
                                roas: formatPaidMediaRoas(item.roas ?? 0),
                                cpa: formatOptionalNumber(item.cpa, formatMoneyShort),
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="paid-media-recommendations__label paid-media-recommendations__label--negative">
                        {t("metaAdsReport.alerts.reduceBudget")}
                      </div>
                      <div className="paid-media-recommendations__stack">
                        {budgetRecommendations.reduce.map(item => (
                          <div
                            key={item.id}
                            className="paid-media-recommendation-card paid-media-recommendation-card--negative"
                          >
                            <strong>{item.campaignName}</strong>
                            <span>
                              {t("metaAdsReport.alerts.recRoasCpa", {
                                roas: formatPaidMediaRoas(item.roas ?? 0),
                                cpa: formatOptionalNumber(item.cpa, formatMoneyShort),
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              </div>
  );
};

export default MetaAlertsPanel;
